import { JSX } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import * as Types from 'types/types';

interface SkillDialogProps {
  open: boolean;
  isMobile: boolean;
  newSkill: Partial<Types.Skill>;
  onSkillChange: (skill: Partial<Types.Skill>) => void;
  onClose: () => void;
  onAdd: () => void;
}

function dialogPaper(isMobile: boolean) {
  return {
    sx: { ...(isMobile && { margin: 0, width: '100%', height: '100%', maxHeight: '100%' }) },
  };
}

function SkillDialog({
  open,
  isMobile,
  newSkill,
  onSkillChange,
  onClose,
  onAdd,
}: SkillDialogProps): JSX.Element {
  const size = isMobile ? 'small' : 'medium';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={dialogPaper(isMobile)}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>Add New Skill</DialogTitle>
      <DialogContent sx={{ overflow: 'auto', pt: { xs: 1, sm: 2 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 0.5, maxWidth: '100%' }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Skill Name"
              value={newSkill.name || ''}
              size={size}
              onChange={(e): void => onSkillChange({ ...newSkill, name: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Category"
              value={newSkill.category || ''}
              size={size}
              placeholder="e.g., Programming, Design, Marketing"
              onChange={(e): void => onSkillChange({ ...newSkill, category: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size={size}>
              <InputLabel>Proficiency Level</InputLabel>
              <Select
                value={newSkill.level || 'beginner'}
                label="Proficiency Level"
                onChange={(e): void =>
                  onSkillChange({ ...newSkill, level: e.target.value as Types.Skill['level'] })
                }
              >
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
                <MenuItem value="expert">Expert</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Years of Experience"
              size={size}
              value={newSkill.yearsOfExperience || 0}
              onChange={(e): void =>
                onSkillChange({ ...newSkill, yearsOfExperience: parseInt(e.target.value) || 0 })
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button onClick={onClose} fullWidth={isMobile} size={size}>
          Cancel
        </Button>
        <Button onClick={onAdd} variant="contained" fullWidth={isMobile} size={size}>
          Add Skill
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface QuestionDialogProps {
  open: boolean;
  isMobile: boolean;
  newQuestion: Partial<Types.CandidateQuestion>;
  onQuestionChange: (q: Partial<Types.CandidateQuestion>) => void;
  onClose: () => void;
  onAdd: () => void;
}

function QuestionDialog(props: QuestionDialogProps): JSX.Element {
  const { open, isMobile, newQuestion, onQuestionChange, onClose, onAdd } = props;
  const size = isMobile ? 'small' : 'medium';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={dialogPaper(isMobile)}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>Add New Question</DialogTitle>
      <DialogContent sx={{ overflow: 'auto', pt: { xs: 1, sm: 2 } }}>
        <TextField
          fullWidth
          multiline
          rows={isMobile ? 3 : 4}
          label="Question"
          size={size}
          value={newQuestion.question || ''}
          onChange={(e): void => onQuestionChange({ ...newQuestion, question: e.target.value })}
        />
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button onClick={onClose} fullWidth={isMobile} size={size}>
          Cancel
        </Button>
        <Button
          onClick={onAdd}
          variant="contained"
          fullWidth={isMobile}
          size={size}
          disabled={!newQuestion.question?.trim()}
        >
          Add Question
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface ExperienceDialogProps {
  open: boolean;
  isMobile: boolean;
  newExperience: Partial<Types.WorkExperience>;
  onExperienceChange: (exp: Partial<Types.WorkExperience>) => void;
  onClose: () => void;
  onAdd: () => void;
}

function ExperienceDialog(props: ExperienceDialogProps): JSX.Element {
  const { open, isMobile, newExperience, onExperienceChange, onClose, onAdd } = props;
  const size = isMobile ? 'small' : 'medium';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={dialogPaper(isMobile)}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>Add Work Experience</DialogTitle>
      <DialogContent sx={{ overflow: 'auto', pt: { xs: 1, sm: 2 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 0.5, maxWidth: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Company Name"
              value={newExperience.companyName || ''}
              size={size}
              onChange={(e): void =>
                onExperienceChange({ ...newExperience, companyName: e.target.value })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Position/Title"
              value={newExperience.position || ''}
              size={size}
              onChange={(e): void =>
                onExperienceChange({ ...newExperience, position: e.target.value })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              size={size}
              value={newExperience.startDate?.toISOString().split('T')[0] || ''}
              onChange={(e): void =>
                onExperienceChange({ ...newExperience, startDate: new Date(e.target.value) })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={newExperience.isCurrent || false}
                  size={size}
                  onChange={(e): void =>
                    onExperienceChange({ ...newExperience, isCurrent: e.target.checked })
                  }
                />
              }
              label="Currently working here"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              label="Job Description"
              size={size}
              value={newExperience.description || ''}
              onChange={(e): void =>
                onExperienceChange({ ...newExperience, description: e.target.value })
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button onClick={onClose} fullWidth={isMobile} size={size}>
          Cancel
        </Button>
        <Button onClick={onAdd} variant="contained" fullWidth={isMobile} size={size}>
          Add Experience
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { ExperienceDialog, QuestionDialog, SkillDialog };
